import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Flex,
  Text,
  Heading,
  Avatar,
  Stack,
  Icon,
  useBreakpointValue,
  Container,
  useToken,
  Skeleton,
  Progress,
  Tooltip,
} from '@chakra-ui/react';
import { FaStar, FaQuoteLeft, FaRegStar } from 'react-icons/fa';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const Reviews = () => {
  const { items: reviews, loading, error } = useSelector((state) => state.reviews);
  const [accent, bgDark, bgCard, textLight, textSecondary] = useToken('colors', [
    'accent',
    'bg-dark',
    'bg-card',
    'text-light',
    'text-secondary',
  ]);

  // Refs para optimización
  const containerRef = useRef(null);
  const lightRef = useRef(null);
  const animationFrameRef = useRef();
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const isHoveringRef = useRef(false);

  // Calcular rating promedio y distribución
  const averageRating =
    reviews.length > 0
      ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
      : 0;

  const ratingDistribution = [0, 0, 0, 0, 0];
  reviews.forEach((review) => {
    ratingDistribution[5 - review.rating]++;
  });

  const totalRatings = reviews.length;
  const percentagePerStar = ratingDistribution.map((count) =>
    totalRatings > 0 ? Math.round((count / totalRatings) * 100) : 0
  );

  const slidesToShow = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  // Configuración optimizada del slider
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 600,
    slidesToShow,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    adaptiveHeight: true,
    cssEase: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
  };

  // Renderizado de estrellas memoizado
  const renderStars = (rating = 0, size = 4) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Icon
          as={i < rating ? FaStar : FaRegStar}
          key={i}
          boxSize={size}
          color={i < rating ? accent : textSecondary}
          _hover={{ transform: 'scale(1.2)' }}
          transition="all 0.2s"
        />
      );
    }
    return stars;
  };

  // Efecto de seguimiento del mouse ultra optimizado
  useEffect(() => {
    const updateLightPosition = (x, y) => {
      if (!lightRef.current) return;

      // Interpolación para suavizado con factor de 0.2 para mejor fluidez
      const dx = x - lastPositionRef.current.x;
      const dy = y - lastPositionRef.current.y;

      lastPositionRef.current = {
        x: lastPositionRef.current.x + dx * 0.2,
        y: lastPositionRef.current.y + dy * 0.2,
      };

      // Usamos transform para máximo rendimiento
      lightRef.current.style.transform = `translate3d(${lastPositionRef.current.x}px, ${lastPositionRef.current.y}px, 0)`;
    };

    const handleMouseMove = (e) => {
      if (!containerRef.current) return;

      // Usamos requestAnimationFrame para sincronizar con los frames
      animationFrameRef.current = requestAnimationFrame(() => {
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        updateLightPosition(x, y);
      });
    };

    const handleMouseEnter = () => {
      isHoveringRef.current = true;
      if (lightRef.current) {
        lightRef.current.style.opacity = '1';
      }
    };

    const handleMouseLeave = () => {
      isHoveringRef.current = false;
      if (lightRef.current) {
        lightRef.current.style.opacity = '0';
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove, { passive: true });
      container.addEventListener('mouseenter', handleMouseEnter, { passive: true });
      container.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      bg={bgDark}
      py={[12, 20]}
      position="relative"
      id="reviews"
      overflow="hidden"
    >
      {/* Luz estática central de fondo */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        w="100%"
        h="100%"
        bg={`radial-gradient(circle at center, rgba(255, 69, 0, 0.05) 0%, transparent 70%)`}
        transform="translate(-50%, -50%)"
        pointerEvents="none"
        zIndex={0}
        willChange="transform"
      />

      {/* Luz dinámica optimizada con transform3d */}
      <Box
        ref={lightRef}
        position="absolute"
        w="300px"
        h="300px"
        borderRadius="full"
        bg={`radial-gradient(circle, rgba(255, 69, 0, 0.1) 0%, transparent 70%)`}
        pointerEvents="none"
        zIndex={0}
        style={{
          willChange: 'transform',
          transform: 'translate3d(-50%, -50%, 0)',
          opacity: 0,
          transition: 'opacity 0.2s ease-out',
          left: '0',
          top: '0',
        }}
      />

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <Box textAlign="center" mb={12}>
          <Text
            color={accent}
            fontWeight="bold"
            letterSpacing="widest"
            textTransform="uppercase"
            fontSize="sm"
            mb={2}
          >
            Testimonios
          </Text>
          <Heading
            as="h2"
            size="xl"
            color={textLight}
            mb={4}
            fontWeight="extrabold"
            lineHeight="1.2"
          >
            Experiencias compartidas
          </Heading>
          <Text maxW="600px" mx="auto" color={textSecondary} fontSize="lg">
            Lo que dicen clientes y colaboradores sobre nuestro trabajo conjunto
          </Text>
        </Box>

        {/* Rating General */}
        <Box
          bg={bgCard}
          p={6}
          borderRadius="xl"
          mb={10}
          boxShadow="lg"
          position="relative"
          zIndex={1}
        >
          <Flex direction={{ base: 'column', md: 'row' }} align="center">
            <Box textAlign="center" mb={{ base: 4, md: 0 }} mr={{ md: 8 }}>
              <Text fontSize="5xl" fontWeight="bold" color={textLight}>
                {averageRating}
              </Text>
              <Flex justify="center" mb={2}>
                {renderStars(Math.round(averageRating), 6)}
              </Flex>
              <Text color={textSecondary}>
                {totalRatings} {totalRatings === 1 ? 'reseña' : 'reseñas'}
              </Text>
            </Box>

            <Box flex="1">
              {[5, 4, 3, 2, 1].map((stars, index) => (
                <Flex key={stars} align="center" mb={2}>
                  <Text color={textLight} minW="80px">
                    {stars} estrellas
                  </Text>
                  <Progress
                    value={percentagePerStar[5 - stars]}
                    flex="1"
                    mx={4}
                    height="10px"
                    borderRadius="full"
                    colorScheme="orange"
                    bg={`${accent}20`}
                  />
                  <Text color={textSecondary} minW="40px" textAlign="right">
                    {percentagePerStar[5 - stars]}%
                  </Text>
                </Flex>
              ))}
            </Box>
          </Flex>
        </Box>

        {loading ? (
          <Flex justify="center" gap={6} wrap="wrap">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height="300px" width="350px" borderRadius="xl" opacity="0.2" />
            ))}
          </Flex>
        ) : error ? (
          <Text color="red.400" textAlign="center">
            Error al cargar reseñas
          </Text>
        ) : (
          <Box position="relative" px={{ base: 0, md: 4 }} zIndex={1}>
            <Slider {...sliderSettings}>
              {reviews.map((rev) => (
                <Box key={rev._id} px={3} py={6}>
                  <Box
                    bg={bgCard}
                    p={8}
                    borderRadius="xl"
                    boxShadow={`0 4px 20px rgba(255, 69, 0, 0.125)`}
                    height="100%"
                    position="relative"
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-5px)',
                      boxShadow: `0 8px 30px rgba(255, 69, 0, 0.1875)`,
                    }}
                    overflow="hidden"
                    zIndex={1}
                  >
                    <Icon
                      as={FaQuoteLeft}
                      boxSize={8}
                      color={accent}
                      opacity={0.1}
                      position="absolute"
                      top={6}
                      right={6}
                    />

                    <Stack spacing={5} h="100%">
                      <Flex align="center" mb={4}>
                        <Avatar
                          name={rev.author || 'A.'}
                          size="md"
                          bg={`rgba(255, 69, 0, 0.125)`}
                          color={accent}
                          border={`2px solid rgba(255, 69, 0, 0.25)`}
                          mr={4}
                        />
                        <Box>
                          <Text fontWeight="bold" color={textLight} fontSize="lg">
                            {rev.author}
                          </Text>
                          <Flex mt={1}>
                            <Tooltip label={`${rev.rating} estrellas`}>
                              <Flex>{renderStars(rev.rating)}</Flex>
                            </Tooltip>
                          </Flex>
                        </Box>
                      </Flex>

                      <Text
                        color={textSecondary}
                        fontStyle="italic"
                        flex="1"
                        fontSize="md"
                        lineHeight="1.7"
                        position="relative"
                        zIndex="1"
                      >
                        "{rev.content}"
                      </Text>

                      {/* <Text fontSize="sm" color={textSecondary} textAlign="right" opacity="0.8">
                        {new Date(rev.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Text> */}
                    </Stack>
                  </Box>
                </Box>
              ))}
            </Slider>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default React.memo(Reviews);
